/** Zod-free project values for the browser; the API validates with projects.ts. */
/** `aktiv` means "commissioned" (beauftragt); `verloren` is a lost acquisition (AC-202, E1). */
export const PROJECT_STATUSES = [
  'akquise',
  'aktiv',
  'abgeschlossen',
  'archiviert',
  'verloren',
] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
/** AC-201 extension (F3): a reference project may be created as completed; archiving is AC-208. */
export const PROJECT_CREATE_STATUSES = ['akquise', 'aktiv', 'abgeschlossen'] as const;
export type ProjectCreateStatus = (typeof PROJECT_CREATE_STATUSES)[number];
/** AC-201 extension (F2): set by the API, never an input. */
export const PROJECT_HERKUNFT = ['manuell', 'pacs'] as const;
export type ProjektHerkunft = (typeof PROJECT_HERKUNFT)[number];
export const PROJECT_FEE_PATTERN = /^(0|[1-9]\d{0,9})(\.\d{1,2})?$/;
export const PROJECT_VERSION_MAX = 2_147_483_646;
export function isProjectStatus(value: string): value is ProjectStatus {
  return (PROJECT_STATUSES as readonly string[]).includes(value);
}
export function isProjectCreateStatus(value: string): value is ProjectCreateStatus {
  return (PROJECT_CREATE_STATUSES as readonly string[]).includes(value);
}

/**
 * The groups that the commissioned phases cover, or `null` if the phases are not a
 * union of WHOLE groups of the tenant configuration (AC-201 extension, F1). No phases
 * means "not recorded" and yields no groups (D2). Phases, not groups, are stored, as in
 * the work package library (AC-301 H2); the groups are derived for display.
 *
 * For the service (check on write) and the form (checkboxes per group) alike.
 */
export function beauftragteLphGruppen<G extends { readonly phases: readonly number[] }>(
  phasen: readonly number[],
  gruppen: readonly G[],
): G[] | null {
  const gesucht = new Set(phasen);
  const getroffen = gruppen.filter((gruppe) => gruppe.phases.some((phase) => gesucht.has(phase)));
  const abgedeckt = getroffen.flatMap((gruppe) => gruppe.phases);
  const ganz = getroffen.every((gruppe) => gruppe.phases.every((phase) => gesucht.has(phase)));
  return ganz && abgedeckt.length === gesucht.size ? getroffen : null;
}
